/**
 * Shared product-sync settings buffer (Phase 65 / BLU-594 PR2).
 *
 * ONE buffer, created at the page level and threaded into BOTH tabs: the Code
 * tables tab edits the code maps + single-size list; the Products tab's tier-3
 * "print sets" edits `darkColours`. Both mutate the same local doc and save
 * through the same mutation, so a dark-colour flip on a product row and a code
 * accept in the mapping screen share one "unsaved changes" state (mirrors
 * `RipRoutingSection`'s local-edit → explicit Save model, DESIGN §4/§9).
 *
 * `savePatch` (BLU-1371) saves a small patch (the onboarding Tag products
 * step's tag + garment choice) in ONE call through the same mutation. It
 * refuses until the server doc has loaded and merges over that doc, so an
 * empty not-yet-hydrated buffer can never overwrite saved code tables.
 *
 * No lost update between quick saves (BLU-1373): the mutation's success
 * handler FIRST writes the server's returned doc into the settings query
 * cache, THEN invalidates as before. So a second `savePatch` issued right
 * after the first succeeds merges over the just-saved doc (not the stale
 * pre-save cache the refetch has not replaced yet), and the hydrate effect
 * sees the saved doc at once. `isFetching` lets a control wait out the
 * refetch too.
 *
 * Only ever commits VALID codes to the buffer (the mapping UI validates on
 * accept/add), so — unlike RipRouting — there is no `anyInvalid` save gate.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  BYO_MAPPINGS_MISSING_MARKER,
  BYO_SWITCH_BACK_LOCKED_MARKER,
  BYO_SWITCH_NOT_ALLOWED_MARKER,
  CODE_TABLE_MAX_ENTRIES,
  EMPTY_PRODUCT_SYNC_SETTINGS,
  matchSettingsCode,
  nameListIncludes,
  type BlankSource,
  type DesignIdSource,
  type ProductSyncSettings,
  type SellsKind,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import type { CodeTable } from '../lib/types';

/**
 * The fields `savePatch` can change. `blankSource: null` / `sells: null`
 * clear the choice; a list replaces the saved list (an empty `mixedTypes` /
 * `customProductIds` clears it).
 */
export interface ProductSyncSettingsPatch {
  syncTag?: string;
  /**
   * Phase 86 slice B (86-19 review C): the design ID source. A patch carrying
   * it saves ONLY that key, applied over the loaded server doc (never the
   * dirty buffer); the buffer's own unsaved edits stay as they are.
   */
  designIdSource?: DesignIdSource;
  blankSource?: BlankSource | null;
  sells?: SellsKind | null;
  customTypes?: string[];
  mixedTypes?: string[];
  customProductIds?: string[];
}

export interface SavePatchOptions {
  /** Runs after THIS save succeeds (on top of the hook's own success handling). */
  onSuccess?: () => void;
  /**
   * The success toast for THIS save, instead of the code tables message
   * (`save.success`). The onboarding steps name their own step; Product sync's
   * own saves pass nothing and keep today's toast.
   */
  successMessage?: string;
}

/**
 * Phase 86 slice B: why the server refused the last save of the design ID
 * source (its message starts with the marker), or null. Cleared by the next
 * save, a successful save and Discard.
 */
export type DesignIdSourceRefusal = 'mappingsMissing' | 'switchBackLocked' | 'notAllowed';

/** The refusal a save error names by its marker prefix, or null for any other error. */
export function designIdSourceRefusalOf(message: string | undefined): DesignIdSourceRefusal | null {
  if (!message) return null;
  if (message.startsWith(`${BYO_MAPPINGS_MISSING_MARKER}:`)) return 'mappingsMissing';
  if (message.startsWith(`${BYO_SWITCH_BACK_LOCKED_MARKER}:`)) return 'switchBackLocked';
  if (message.startsWith(`${BYO_SWITCH_NOT_ALLOWED_MARKER}:`)) return 'notAllowed';
  return null;
}

/** The ONE copy key per refusal (the Design IDs control and the toast both read it). Literal keys, so the i18n check sees them. */
export const REFUSAL_KEY: Record<DesignIdSourceRefusal, string> = {
  mappingsMissing: 'designIds.refusal.mappingsMissing',
  switchBackLocked: 'designIds.refusal.switchBackLocked',
  notAllowed: 'designIds.refusal.notAllowed',
};

export interface ProductSyncSettingsBuffer {
  /** The buffered (possibly-dirty) settings the UI renders + saves. */
  settings: ProductSyncSettings;
  /** Last-known server settings (target of Discard). */
  serverSettings: ProductSyncSettings;
  hasChanges: boolean;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  isSaving: boolean;
  /** The settings query is fetching (e.g. the refetch after a save). */
  isFetching: boolean;
  refetch: () => void;

  /** Add or update `name → code` in a code table (code is trimmed + uppercased). */
  setCode: (table: CodeTable, name: string, code: string) => void;
  /** Remove a name from a code table. */
  removeCode: (table: CodeTable, name: string) => void;
  /** Set the colour-less placeholder code (trimmed + uppercased). */
  setColourlessCode: (code: string) => void;
  /** Set the Shopify gate tag for the candidate list (trimmed; '' = no gate). */
  setSyncTag: (tag: string) => void;

  /** Flip a colour's dark-set membership. Optional `next` forces the direction. */
  toggleDarkColour: (name: string, next?: boolean) => void;
  /** Merge colour names into the dark set (auto-detect). Returns how many were newly added. */
  addDarkColours: (names: readonly string[]) => number;
  /** Flip a product type's single-size membership. Optional `next` forces the direction. */
  toggleSingleSizeType: (name: string, next?: boolean) => void;
  /**
   * Flip a product type's on-demand-custom membership (derived SKUs get the
   * `CUST-` wrapper). Optional `next` forces the direction. When turning custom
   * ON and the type has no base code yet, inherits a mapped `Custom X → X`
   * sibling's code (Option 1); never overwrites a code the admin already set.
   */
  toggleCustomType: (name: string, next?: boolean) => void;
  /** The server's refusal of the last design ID source save, shown inline by the control. */
  designIdSourceRefusal: DesignIdSourceRefusal | null;
  /** Clear a stale refusal (the control calls it when the admin picks again). */
  clearDesignIdSourceRefusal: () => void;

  save: () => void;
  /**
   * Merge `patch` over the loaded doc and save it in one call. Returns false
   * (and saves nothing) while the server doc has not loaded or a save is
   * pending.
   */
  savePatch: (patch: ProductSyncSettingsPatch, options?: SavePatchOptions) => boolean;
  discard: () => void;
}

/**
 * The wire shape of a save: `blankSource`, `sells`, `mixedTypes` and
 * `customProductIds` are ALWAYS sent (the value, or null / []), so a current
 * build never triggers the server's carry-forward read and an unset value is
 * an explicit clear.
 */
function toSaveInput(settings: ProductSyncSettings, designIdSource?: DesignIdSource) {
  // Phase 86 slice B (86-19 review C): the design ID source is not part of
  // the page buffer. Only its own save sends it; every other save omits it,
  // so the server carries the saved value (and its stamp) forward.
  const { designIdSource: _source, designIdSourceSwitchedOnAt: _stamp, ...rest } = settings;
  return {
    settings: {
      ...rest,
      ...(designIdSource ? { designIdSource } : {}),
      blankSource: settings.blankSource ?? null,
      sells: settings.sells ?? null,
      mixedTypes: settings.mixedTypes ?? [],
      customProductIds: settings.customProductIds ?? [],
    },
  };
}

/**
 * Apply a patch to a settings doc. A null `blankSource` / `sells` removes the
 * key; a list replaces the saved one, and an empty `mixedTypes` /
 * `customProductIds` is left off the doc (mirrors the normalizer).
 */
function applyPatch(base: ProductSyncSettings, patch: ProductSyncSettingsPatch): ProductSyncSettings {
  const { blankSource: _blank, sells: _sells, mixedTypes: _mixed, customProductIds: _ids, designIdSource: _source, ...rest } = base;
  const next: ProductSyncSettings = { ...rest };
  if (patch.syncTag !== undefined) next.syncTag = patch.syncTag;
  if (patch.customTypes !== undefined) next.customTypes = patch.customTypes;
  const blankSource = patch.blankSource === undefined ? base.blankSource : patch.blankSource;
  if (blankSource) next.blankSource = blankSource;
  const sells = patch.sells === undefined ? base.sells : patch.sells;
  if (sells) next.sells = sells;
  const mixedTypes = patch.mixedTypes ?? base.mixedTypes;
  if (mixedTypes && mixedTypes.length > 0) next.mixedTypes = mixedTypes;
  const customProductIds = patch.customProductIds ?? base.customProductIds;
  if (customProductIds && customProductIds.length > 0) next.customProductIds = customProductIds;
  const designIdSource = patch.designIdSource ?? base.designIdSource;
  if (designIdSource) next.designIdSource = designIdSource;
  return next;
}

/** Case-insensitive removal of a name from a list, preserving other entries. */
function removeFromList(list: string[], name: string): string[] {
  const key = name.trim().toLowerCase();
  return list.filter((entry) => entry.trim().toLowerCase() !== key);
}

/**
 * Option-1 sibling lookup: for a `Custom X` product-type name, return the base
 * code of a plausible standard sibling `X` (the same name minus a single leading
 * `Custom `/`custom ` word) if that sibling is already mapped, else null. Stored
 * codes always pass `CODE_RE`, so a non-null result is a real, non-empty code.
 */
function siblingBaseCode(productTypes: Record<string, string>, name: string): string | null {
  const match = /^custom\s+(.+)$/i.exec(name.trim());
  const siblingName = match?.[1]?.trim();
  if (!siblingName) return null;
  return matchSettingsCode(productTypes, siblingName);
}

/** Case-insensitive removal of a key from a code map, preserving other entries. */
function removeFromMap(map: Record<string, string>, name: string): Record<string, string> {
  const key = name.trim().toLowerCase();
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    if (k.trim().toLowerCase() !== key) out[k] = v;
  }
  return out;
}

export function useProductSyncSettingsBuffer(): ProductSyncSettingsBuffer {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const settingsQuery = trpc.productSync.getProductSyncSettings.useQuery();

  const [local, setLocal] = useState<ProductSyncSettings>(EMPTY_PRODUCT_SYNC_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);
  const [designIdSourceRefusal, setDesignIdSourceRefusal] = useState<DesignIdSourceRefusal | null>(null);

  const serverSettings: ProductSyncSettings = settingsQuery.data?.settings ?? EMPTY_PRODUCT_SYNC_SETTINGS;

  // Phase 86 slice B (86-19 review C): set by a design ID source save made
  // while the buffer holds unsaved edits. Until the next page save, patch or
  // Discard, hydrating takes ONLY the design ID keys from the server, so those
  // edits survive (and stay unsaved).
  const keepLocalEdits = useRef(false);

  // Hydrate from the server on load + after save invalidation. React Query's
  // structural sharing keeps a stable reference when deeply equal, so this only
  // re-fires on a real change (mirrors RipRoutingSection).
  useEffect(() => {
    const data = settingsQuery.data;
    if (!data) return;
    if (keepLocalEdits.current) {
      const { designIdSource, designIdSourceSwitchedOnAt } = data.settings;
      setLocal((prev) => {
        const { designIdSource: _source, designIdSourceSwitchedOnAt: _stamp, ...rest } = prev;
        return {
          ...rest,
          ...(designIdSource ? { designIdSource } : {}),
          ...(designIdSourceSwitchedOnAt ? { designIdSourceSwitchedOnAt } : {}),
        };
      });
      return;
    }
    setLocal(data.settings);
    setHasChanges(false);
  }, [settingsQuery.data]);

  // The toast text for the NEXT mutate, set by `save` / `savePatch` right
  // before it. `onMutate` moves it into that save's own context, so each
  // save's success reads its own message.
  const patchSuccessMessage = useRef<string | undefined>(undefined);

  const updateMutation = trpc.productSync.updateProductSyncSettings.useMutation({
    onMutate: () => {
      setDesignIdSourceRefusal(null);
      const successMessage = patchSuccessMessage.current;
      patchSuccessMessage.current = undefined;
      return { successMessage };
    },
    onSuccess: (data, _variables, context) => {
      // FIRST the saved doc into the cache (see the module docblock: no lost
      // update between quick saves), THEN the usual invalidations.
      utils.productSync.getProductSyncSettings.setData(undefined, { exists: true, settings: data.settings });
      // A savePatch caller's own message applies to its save only.
      toast.success(context?.successMessage ?? t('save.success'));
      // A design ID source save made over unsaved edits leaves them unsaved.
      if (!keepLocalEdits.current) setHasChanges(false);
      void utils.productSync.getProductSyncSettings.invalidate();
      void utils.productSync.getProductSyncPreview.invalidate();
      void utils.productSync.getProductSyncCoverage.invalidate();
    },
    onError: (e) => {
      // Phase 86 slice B: the design ID source refusals carry a marker (one
      // of them is also PRECONDITION_FAILED, so this runs first). The control
      // shows it inline; the toast says the same in the operator's language.
      const refusal = designIdSourceRefusalOf(e.message);
      if (refusal) {
        setDesignIdSourceRefusal(refusal);
        toast.error(t(REFUSAL_KEY[refusal]));
        return;
      }
      // The server refuses a save when the stored doc already holds more
      // entries than the cap (saving would delete them). Its own words, not
      // the English server message, so Thread Lab reads it in Chinese.
      if (trpcErrorCode(e) === 'PRECONDITION_FAILED') {
        toast.error(t('save.errorOverCap', { tableMax: CODE_TABLE_MAX_ENTRIES.toLocaleString() }));
        return;
      }
      toast.error(t('save.error', { message: e.message }));
    },
  });

  const setCode = useCallback((table: CodeTable, name: string, code: string) => {
    const cleanName = name.trim();
    const cleanCode = code.trim().toUpperCase();
    if (!cleanName) return;
    setLocal((prev) => ({ ...prev, [table]: { ...prev[table], [cleanName]: cleanCode } }));
    setHasChanges(true);
  }, []);

  const removeCode = useCallback((table: CodeTable, name: string) => {
    setLocal((prev) => ({ ...prev, [table]: removeFromMap(prev[table], name) }));
    setHasChanges(true);
  }, []);

  const setColourlessCode = useCallback((code: string) => {
    setLocal((prev) => ({ ...prev, colourlessCode: code.trim().toUpperCase() }));
    setHasChanges(true);
  }, []);

  const setSyncTag = useCallback((tag: string) => {
    // No trim here — trimming while typing eats the space between words of a
    // multi-word tag. The save path (zod .trim() + normalizeSyncTag) trims.
    setLocal((prev) => ({ ...prev, syncTag: tag }));
    setHasChanges(true);
  }, []);

  const toggleDarkColour = useCallback((name: string, next?: boolean) => {
    const cleanName = name.trim();
    if (!cleanName) return;
    setLocal((prev) => {
      const isDark = nameListIncludes(prev.darkColours, cleanName);
      const shouldBeDark = next ?? !isDark;
      if (shouldBeDark === isDark) return prev;
      return {
        ...prev,
        darkColours: shouldBeDark
          ? [...prev.darkColours, cleanName]
          : removeFromList(prev.darkColours, cleanName),
      };
    });
    setHasChanges(true);
  }, []);

  const addDarkColours = useCallback((names: readonly string[]): number => {
    let added = 0;
    setLocal((prev) => {
      const next = [...prev.darkColours];
      for (const raw of names) {
        const name = raw.trim();
        if (!name || nameListIncludes(next, name)) continue;
        next.push(name);
        added += 1;
      }
      if (added === 0) return prev;
      return { ...prev, darkColours: next };
    });
    if (added > 0) setHasChanges(true);
    return added;
  }, []);

  const toggleSingleSizeType = useCallback((name: string, next?: boolean) => {
    const cleanName = name.trim();
    if (!cleanName) return;
    setLocal((prev) => {
      const isSingle = nameListIncludes(prev.singleSizeTypes, cleanName);
      const shouldBeSingle = next ?? !isSingle;
      if (shouldBeSingle === isSingle) return prev;
      return {
        ...prev,
        singleSizeTypes: shouldBeSingle
          ? [...prev.singleSizeTypes, cleanName]
          : removeFromList(prev.singleSizeTypes, cleanName),
      };
    });
    setHasChanges(true);
  }, []);

  const toggleCustomType = useCallback((name: string, next?: boolean) => {
    const cleanName = name.trim();
    if (!cleanName) return;
    setLocal((prev) => {
      const isCustom = nameListIncludes(prev.customTypes, cleanName);
      const shouldBeCustom = next ?? !isCustom;
      if (shouldBeCustom === isCustom) return prev;

      const customTypes = shouldBeCustom
        ? [...prev.customTypes, cleanName]
        : removeFromList(prev.customTypes, cleanName);

      // Option 1 convenience: when marking a `Custom …` type custom and it has
      // no base code yet, inherit a mapped sibling's code so it derives
      // `CUST-{siblingCode}-…`. Never overwrite a code the admin already set.
      let productTypes = prev.productTypes;
      if (shouldBeCustom && !matchSettingsCode(prev.productTypes, cleanName)) {
        const inherited = siblingBaseCode(prev.productTypes, cleanName);
        if (inherited) productTypes = { ...prev.productTypes, [cleanName]: inherited };
      }

      return { ...prev, customTypes, productTypes };
    });
    setHasChanges(true);
  }, []);

  const clearDesignIdSourceRefusal = useCallback(() => setDesignIdSourceRefusal(null), []);

  const save = useCallback(() => {
    keepLocalEdits.current = false;
    patchSuccessMessage.current = undefined;
    updateMutation.mutate(toSaveInput(local));
  }, [updateMutation, local]);

  const savePatch = useCallback(
    (patch: ProductSyncSettingsPatch, options?: SavePatchOptions): boolean => {
      const loaded = settingsQuery.data?.settings;
      if (!loaded || updateMutation.isPending) return false;
      const onSuccess = options?.onSuccess;
      patchSuccessMessage.current = options?.successMessage;
      const mutateOptions = onSuccess ? { onSuccess: () => onSuccess() } : undefined;
      if (patch.designIdSource !== undefined) {
        // The design ID source saves alone, over the LOADED server doc: the
        // buffer's unsaved edits are neither sent nor dropped.
        keepLocalEdits.current = hasChanges;
        updateMutation.mutate(toSaveInput(loaded, patch.designIdSource), mutateOptions);
        return true;
      }
      keepLocalEdits.current = false;
      const next = applyPatch(hasChanges ? local : loaded, patch);
      setLocal(next);
      updateMutation.mutate(toSaveInput(next), mutateOptions);
      return true;
    },
    [settingsQuery.data, updateMutation, hasChanges, local],
  );

  const discard = useCallback(() => {
    keepLocalEdits.current = false;
    setLocal(serverSettings);
    setHasChanges(false);
    setDesignIdSourceRefusal(null);
  }, [serverSettings]);

  const refetch = useCallback(() => {
    void settingsQuery.refetch();
  }, [settingsQuery]);

  return useMemo(
    () => ({
      settings: local,
      serverSettings,
      hasChanges,
      isLoading: settingsQuery.isLoading,
      isError: settingsQuery.isError,
      errorMessage: settingsQuery.error?.message ?? null,
      isSaving: updateMutation.isPending,
      isFetching: settingsQuery.isFetching,
      refetch,
      setCode,
      removeCode,
      setColourlessCode,
      setSyncTag,
      toggleDarkColour,
      addDarkColours,
      toggleSingleSizeType,
      toggleCustomType,
      designIdSourceRefusal,
      clearDesignIdSourceRefusal,
      save,
      savePatch,
      discard,
    }),
    [
      local,
      serverSettings,
      hasChanges,
      settingsQuery.isLoading,
      settingsQuery.isError,
      settingsQuery.error?.message,
      updateMutation.isPending,
      settingsQuery.isFetching,
      refetch,
      setCode,
      removeCode,
      setColourlessCode,
      setSyncTag,
      toggleDarkColour,
      addDarkColours,
      toggleSingleSizeType,
      toggleCustomType,
      designIdSourceRefusal,
      clearDesignIdSourceRefusal,
      save,
      savePatch,
      discard,
    ],
  );
}
